import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { X, CheckCircle2, ArrowRight, ArrowLeft, Sparkles, Home, Sofa, HelpCircle } from "lucide-react";
import { Slider } from "@/components/ui/slider";
import { base44 } from "@/api/base44Client";
import { trackFormEvent } from "@/lib/formTracking";
import useAddressAutocomplete from "@/hooks/useAddressAutocomplete";

const FORM_NAME = "Interiors Quote";
const TOTAL_STEPS = 3;
const LIVING_ROOM_IMG = "https://media.base44.com/images/public/6a316287a1be7d9945ac7d10/45e93a3db_generated_image.png";

const SERVICE_LEVELS = [
  { id: "full", icon: Sparkles, label: "Full design & staging", desc: "Empty property — designed and fully furnished from scratch" },
  { id: "touches", icon: Home, label: "Finishing touches", desc: "Already furnished, needs styling and all the small bits" },
  { id: "key", icon: Sofa, label: "Key pieces only", desc: "A few statement items or a light refresh" },
  { id: "advice", icon: HelpCircle, label: "Not sure yet", desc: "I'd like some advice on what's needed first" },
];

const BED_LABELS = ["Studio", "1 bed", "2 beds", "3 beds", "4 beds", "5 beds", "6 beds", "7+ beds"];
const bedToNum = (s) => (BED_LABELS.indexOf(s) >= 0 ? BED_LABELS.indexOf(s) : 2);
const numToBed = (n) => BED_LABELS[n] || "2 beds";

const inputStyle = {
  border: "1.5px solid #E5E5E5", borderRadius: 10, padding: "13px 16px",
  fontFamily: "'DM Sans', sans-serif", fontSize: 15, color: "#1A1A1A",
  background: "#fff", width: "100%", boxSizing: "border-box", outline: "none",
  transition: "border-color 0.2s",
};
const labelStyle = {
  fontFamily: "'DM Sans', sans-serif", fontSize: 11, fontWeight: 600,
  letterSpacing: "0.07em", textTransform: "uppercase", color: "#888",
  display: "block", marginBottom: 6, textAlign: "left",
};
const errStyle = { color: "#4A6DC7", fontSize: 11, marginTop: 4, fontFamily: "'DM Sans', sans-serif" };

export default function InteriorsQuoteModal({ onClose, onSubmitted }) {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({
    serviceLevel: "", address: "", bedNum: 2, bedrooms: "2 beds",
    startDate: "", fullName: "", email: "", phoneCode: "+44", phone: "", comments: "",
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const addressRef = useRef(null);
  const advanceTimer = useRef(null);
  const hasSubmittedRef = useRef(false);

  const setF = (f) => (e) => {
    setForm((p) => ({ ...p, [f]: e.target.value }));
    if (errors[f]) setErrors((prev) => ({ ...prev, [f]: undefined }));
  };

  const validatePhone = (phone) => {
    const d = phone.replace(/\s/g, "").replace(/-/g, "");
    return /^((\+44|0044|0)[1-9][0-9]{7,9})$/.test(d) || (d.length >= 10 && d.length <= 15);
  };

  const validateStep = (s) => {
    const e = {};
    if (s === 0) { if (!form.serviceLevel) e.serviceLevel = "Please choose one"; }
    else if (s === 1) {
      if (!form.address.trim()) e.address = "Address is required";
      if (!form.bedrooms) e.bedrooms = "Required";
      if (!form.startDate) e.startDate = "Required";
    } else if (s === 2) {
      if (!form.fullName.trim()) e.fullName = "Name is required";
      if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Valid email is required";
      if (!form.phone.trim() || !validatePhone(form.phone)) e.phone = "Valid UK phone number required";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  useAddressAutocomplete(addressRef, (addr) => {
    setForm((p) => ({ ...p, address: addr }));
    if (errors.address) setErrors((prev) => ({ ...prev, address: undefined }));
  }, { deps: [step] });

  const selectAndAdvance = (value, nextStep) => {
    setForm((p) => ({ ...p, serviceLevel: value }));
    if (errors.serviceLevel) setErrors((prev) => ({ ...prev, serviceLevel: undefined }));
    clearTimeout(advanceTimer.current);
    advanceTimer.current = setTimeout(() => setStep(nextStep), 280);
  };

  const next = () => { if (validateStep(step) && step < TOTAL_STEPS - 1) setStep(step + 1); };
  const back = () => { clearTimeout(advanceTimer.current); if (step > 0) setStep(step - 1); };

  const serviceLabel = (id) => (SERVICE_LEVELS.find((s) => s.id === id) || {}).label || "";

  const buildEmailBody = () => [
    "=== INTERIORS QUOTE REQUEST ===",
    `Name: ${form.fullName}`,
    `Email: ${form.email}`,
    `Phone: ${form.phoneCode} ${form.phone}`,
    `Service level: ${serviceLabel(form.serviceLevel)}`,
    `Property address: ${form.address}`,
    `Bedrooms: ${form.bedrooms}`,
    `Date to start furnishing: ${form.startDate}`,
    `Comments: ${form.comments || "None"}`,
  ].join("\n");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateStep(step)) return;
    setSubmitting(true);
    hasSubmittedRef.current = true;
    try {
      await base44.functions.invoke("sendLeadEmail", {
        subject: `New interiors quote – ${form.fullName}`,
        body: buildEmailBody(),
      });
    } catch (_) {}
    try {
      await base44.functions.invoke("saveLeadToSheet", {
        formData: {
          fullName: form.fullName,
          email: form.email,
          phone: form.phone,
          phoneCode: form.phoneCode,
          address: form.address,
          bedrooms: form.bedrooms,
          mode: "interiors",
          serviceLevel: serviceLabel(form.serviceLevel),
          startDate: form.startDate,
          comments: form.comments,
        },
        uploadedFiles: [],
      });
    } catch (_) {}
    if (form.email) {
      try {
        await base44.functions.invoke("sendConfirmationEmail", {
          to: form.email, name: form.fullName, enquiryType: "interiors",
          fields: [
            { label: "Name", value: form.fullName },
            { label: "Email", value: form.email },
            { label: "Phone", value: `${form.phoneCode} ${form.phone}` },
            { label: "Service level", value: serviceLabel(form.serviceLevel) },
            { label: "Property address", value: form.address },
            { label: "Bedrooms", value: form.bedrooms },
            { label: "Date to start furnishing", value: form.startDate },
            { label: "Comments", value: form.comments || "—" },
          ],
        });
      } catch (_) {}
    }
    trackFormEvent(FORM_NAME, true);
    setSubmitting(false);
    setSubmitted(true);
    setTimeout(() => onSubmitted(), 1400);
  };

  const handleClose = () => {
    if (!hasSubmittedRef.current) trackFormEvent(FORM_NAME, false);
    onClose();
  };

  const isLastStep = step === TOTAL_STEPS - 1;
  const intros = [
    "Let's start with the level of service you need.",
    "Tell us about the property we'll be working with.",
    "Last step — where should we send your quote?",
  ];

  return (
    <ModalShell onClose={handleClose}>
      <div className="iq-two-col" style={{ display: "grid", gridTemplateColumns: "1.15fr 1fr", gap: 0, background: "#fff", borderRadius: 16, overflow: "hidden", minHeight: 540 }}>
        {/* Form side */}
        <div className="iq-form-side" style={{ padding: "44px 40px", display: "flex", flexDirection: "column" }}>
          <div style={{ marginBottom: 24 }}>
            <div style={{ marginBottom: 16 }}>
              <img src="https://media.base44.com/images/public/6a316287a1be7d9945ac7d10/24682ceeb_TruestaysLogo.png" alt="Truestays" style={{ height: 20, width: "auto" }} />
            </div>
            <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 13, color: "#888", margin: "0 0 10px" }}>{intros[step]}</p>
            <div style={{ display: "flex", gap: 6 }}>
              {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
                <div key={i} style={{ flex: 1, height: 4, borderRadius: 2, background: i <= step ? "#000" : "#E0E0E0", transition: "background 0.3s" }} />
              ))}
            </div>
          </div>

          <div style={{ flex: 1 }}>
            {step === 0 && (
              <div>
                <h3 style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: 22, color: "#1A1A1A", margin: "0 0 16px", letterSpacing: "-0.01em" }}>What level of service do you need?</h3>
                {errors.serviceLevel && <p style={errStyle}>{errors.serviceLevel}</p>}
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {SERVICE_LEVELS.map((s) => {
                    const sel = form.serviceLevel === s.id; const Icon = s.icon;
                    return (
                      <button key={s.id} type="button" onClick={() => selectAndAdvance(s.id, 1)}
                        style={{ display: "flex", alignItems: "flex-start", gap: 14, padding: "15px 18px", border: sel ? "2px solid #000" : "1px solid #E5E5E5", borderRadius: 12, background: sel ? "#FAFAFA" : "#fff", cursor: "pointer", textAlign: "left", transition: "all 0.15s" }}>
                        <div style={{ width: 38, height: 38, borderRadius: 9, background: "#F5F5F5", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                          <Icon size={18} color="#424242" />
                        </div>
                        <div style={{ flex: 1 }}>
                          <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 15, color: "#1A1A1A", fontWeight: 600, display: "block" }}>{s.label}</span>
                          <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 13, color: "#888", lineHeight: 1.4 }}>{s.desc}</span>
                        </div>
                        {sel && <CheckCircle2 size={18} color="#000" style={{ marginLeft: "auto", flexShrink: 0, marginTop: 2 }} />}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {step === 1 && (
              <div>
                <h3 style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: 22, color: "#1A1A1A", margin: "0 0 16px", letterSpacing: "-0.01em" }}>Tell us about your property</h3>
                <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                  <div>
                    <label style={labelStyle}>Property address *</label>
                    <input ref={addressRef} style={{ ...inputStyle, borderColor: errors.address ? "#4A6DC7" : "#E5E5E5" }} value={form.address} onChange={setF("address")} placeholder="Start typing your address…" autoComplete="off" />
                    {errors.address && <p style={errStyle}>{errors.address}</p>}
                  </div>
                  <div>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 10 }}>
                      <label style={{ ...labelStyle, marginBottom: 0 }}>Bedrooms *</label>
                      <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 15, fontWeight: 700, color: "#1A1A1A" }}>{numToBed(form.bedNum)}</span>
                    </div>
                    <Slider className="iq-bedrooms-slider" value={[form.bedNum]} onValueChange={(v) => { setForm((p) => ({ ...p, bedNum: v[0], bedrooms: numToBed(v[0]) })); if (errors.bedrooms) setErrors((prev) => ({ ...prev, bedrooms: undefined })); }} min={0} max={7} step={1} />
                    <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8 }}>
                      {["Studio", "1", "2", "3", "4", "5", "6", "7+"].map((l, i) => (
                        <span key={i} style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 10, color: i === form.bedNum ? "#000" : "#BBB", fontWeight: i === form.bedNum ? 700 : 400 }}>{l}</span>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label style={labelStyle}>Date to start furnishing from *</label>
                    <input type="date" style={{ ...inputStyle, borderColor: errors.startDate ? "#4A6DC7" : "#E5E5E5" }} value={form.startDate} onChange={setF("startDate")} />
                    {errors.startDate && <p style={errStyle}>{errors.startDate}</p>}
                  </div>
                </div>
              </div>
            )}

            {step === 2 && (
              <form onSubmit={handleSubmit}>
                <h3 style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: 22, color: "#1A1A1A", margin: "0 0 6px", letterSpacing: "-0.01em" }}>Where should we send your quote?</h3>
                <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 13, color: "#888", margin: "0 0 16px", lineHeight: 1.5 }}>
                  Add your details and any notes below — we'll send a bespoke interiors quote within 24 hours.
                </p>
                <div style={{ display: "flex", flexDirection: "column", gap: 11 }}>
                  <div>
                    <label style={labelStyle}>Name *</label>
                    <input style={{ ...inputStyle, borderColor: errors.fullName ? "#4A6DC7" : "#E5E5E5" }} value={form.fullName} onChange={setF("fullName")} placeholder="Your name" />
                    {errors.fullName && <p style={errStyle}>{errors.fullName}</p>}
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 11 }} className="iq-grid">
                    <div>
                      <label style={labelStyle}>Email *</label>
                      <input style={{ ...inputStyle, borderColor: errors.email ? "#4A6DC7" : "#E5E5E5" }} type="email" value={form.email} onChange={setF("email")} placeholder="Email address" />
                      {errors.email && <p style={errStyle}>{errors.email}</p>}
                    </div>
                    <div>
                      <label style={labelStyle}>Phone *</label>
                      <input style={{ ...inputStyle, borderColor: errors.phone ? "#4A6DC7" : "#E5E5E5" }} type="tel" value={form.phone} onChange={setF("phone")} placeholder="07XXX XXXXXX" />
                      {errors.phone && <p style={errStyle}>{errors.phone}</p>}
                    </div>
                  </div>
                  <div>
                    <label style={labelStyle}>Comments <span style={{ fontWeight: 400, textTransform: "none", letterSpacing: 0 }}>(optional)</span></label>
                    <textarea style={{ ...inputStyle, minHeight: 80, resize: "vertical", fontFamily: "'DM Sans', sans-serif" }} value={form.comments} onChange={setF("comments")} placeholder="Tell us about your style, budget, or any special requirements…" />
                  </div>
                  <button type="submit" disabled={submitting}
                    style={{ marginTop: 4, background: "#000", color: "#fff", border: "none", borderRadius: 10, padding: "15px", fontFamily: "'DM Sans', sans-serif", fontSize: 15, fontWeight: 700, width: "100%", cursor: "pointer", transition: "background 0.2s", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8 }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = "#112215")}
                    onMouseLeave={(e) => (e.currentTarget.style.background = "#000")}>
                    {submitting ? "Sending…" : <>Get my interiors quote <ArrowRight size={16} /></>}
                  </button>
                  {submitted && (
                    <div style={{ marginTop: 12, padding: "14px", borderRadius: 10, background: "#F5F5F5", display: "flex", alignItems: "center", gap: 10 }}>
                      <CheckCircle2 size={18} color="#000" />
                      <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: "#1A1A1A", fontWeight: 500 }}>All done! Redirecting…</span>
                    </div>
                  )}
                </div>
              </form>
            )}
          </div>

          {!isLastStep && (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 22 }}>
              <div>
                {step > 0 && (
                  <button type="button" onClick={back} style={{ background: "none", border: "none", color: "#888", fontFamily: "'DM Sans', sans-serif", fontSize: 14, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6 }}>
                    <ArrowLeft size={15} /> Back
                  </button>
                )}
              </div>
              {step < TOTAL_STEPS - 1 && step > 0 && (
                <button type="button" onClick={next} style={{ background: "#000", color: "#fff", border: "none", borderRadius: 10, padding: "12px 28px", fontFamily: "'DM Sans', sans-serif", fontSize: 14, fontWeight: 600, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 8, transition: "background 0.2s" }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = "#112215")}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "#000")}>
                  Next <ArrowRight size={15} />
                </button>
              )}
            </div>
          )}
        </div>

        {/* Image side */}
        <div className="iq-info-side" style={{ position: "relative", overflow: "hidden" }}>
          <img src={LIVING_ROOM_IMG} alt="Stylish calm living room" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
        </div>
      </div>
    </ModalShell>
  );
}

function ModalShell({ children, onClose }) {
  return createPortal(
    <div className="iq-overlay"
      style={{ position: "fixed", inset: 0, zIndex: 9999, background: "rgba(0,0,0,0.55)", backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)", display: "flex", alignItems: "center", justifyContent: "center", padding: "20px" }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="iq-shell" style={{ width: "100%", maxWidth: 920, maxHeight: "92vh", boxShadow: "0 24px 80px rgba(0,0,0,0.3)", position: "relative", display: "flex", flexDirection: "column" }}>
        <style>{`
          .iq-bedrooms-slider > span:first-child { background-color: #E0E0E0 !important; }
          .iq-bedrooms-slider > span:first-child > span { background-color: #000 !important; }
          .iq-bedrooms-slider > span:last-child { background-color: #fff !important; border-color: #000 !important; border-width: 2px !important; border-radius: 9999px !important; }
          @media (max-width: 768px) {
            .iq-overlay { padding: 0 !important; align-items: stretch !important; background: rgba(0,0,0,0.4) !important; backdrop-filter: none !important; -webkit-backdrop-filter: none !important; }
            .iq-shell { max-width: 100% !important; width: 100% !important; height: 100dvh !important; max-height: 100dvh !important; border-radius: 0 !important; }
            .iq-two-col { grid-template-columns: 1fr !important; height: 100dvh !important; border-radius: 0 !important; }
            .iq-info-side { display: none !important; }
            .iq-form-side { padding: 44px 18px 18px 18px !important; height: 100dvh !important; overflow: hidden !important; display: flex !important; flex-direction: column !important; box-sizing: border-box !important; }
          }
          @media (max-width: 600px) { .iq-grid { grid-template-columns: 1fr !important; } }
          @media (max-width: 480px) {
            .iq-form-side { padding: 40px 16px 16px 16px !important; }
            .iq-shell { width: 100vw !important; max-width: 100vw !important; }
            .iq-form-side input, .iq-form-side select, .iq-form-side textarea { width: 100% !important; box-sizing: border-box !important; }
          }
        `}</style>
        <button onClick={onClose} className="iq-close"
          style={{ position: "absolute", top: 16, right: 16, zIndex: 10, background: "#F5F5F5", border: "none", borderRadius: "50%", width: 32, height: 32, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <X size={16} color="#888" />
        </button>
        <div style={{ flex: 1, overflowY: "auto" }}>
          {children}
        </div>
      </div>
    </div>,
    document.body
  );
}